import { useState } from "react";
import { NodeViewProps, NodeViewWrapper } from "@tiptap/react";
import {
  ActionIcon,
  Popover,
  Select,
  Stack,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconPencil } from "@tabler/icons-react";
import classes from "./api-reference.module.css";

const METHODS = ["get", "post", "put", "patch", "delete"] as const;
type Method = (typeof METHODS)[number];

const METHOD_BG: Record<Method, string> = {
  get: "#10b981",
  post: "#3b82f6",
  put: "#f59e0b",
  patch: "#8b5cf6",
  delete: "#ef4444",
};

function normalizeMethod(v: unknown): Method {
  if (typeof v !== "string") return "get";
  const lower = v.toLowerCase() as Method;
  return (METHODS as readonly string[]).includes(lower) ? lower : "get";
}

function splitUrl(
  url: string,
): Array<{ text: string; kind: "path" | "text" }> {
  if (!url) return [];
  const parts = url.split(/(\{[^}]+\})/g).filter(Boolean);
  return parts.map((p) =>
    p.startsWith("{") && p.endsWith("}")
      ? { text: p, kind: "path" as const }
      : { text: p, kind: "text" as const },
  );
}

export default function ApiReferenceView(props: NodeViewProps) {
  const { node, editor, updateAttributes, selected } = props;
  const method = normalizeMethod(node.attrs.method);
  const url =
    typeof node.attrs.url === "string" ? (node.attrs.url as string) : "";
  const [opened, setOpened] = useState(false);
  const canEdit = editor.isEditable;
  const parts = splitUrl(url);

  return (
    <NodeViewWrapper
      className={classes.wrapper}
      data-type="api-reference"
      data-method={method}
      data-selected={selected ? "true" : undefined}
      aria-label={`${method.toUpperCase()} ${url}`}
    >
      <div className={classes.block}>
        <span
          className={classes.badge}
          style={{ backgroundColor: METHOD_BG[method] }}
        >
          {method.toUpperCase()}
        </span>
        <span className={classes.url}>
          {parts.length === 0 ? (
            <span className={classes.placeholder}>
              https://api.example.com/{`{param}`}
            </span>
          ) : (
            parts.map((p, i) =>
              p.kind === "path" ? (
                <span key={i} className={classes.param}>
                  {p.text}
                </span>
              ) : (
                <span key={i}>{p.text}</span>
              ),
            )
          )}
        </span>
        {canEdit && (
          <Popover
            opened={opened}
            onChange={setOpened}
            position="bottom-end"
            withArrow
            shadow="md"
            width={360}
            trapFocus
          >
            <Popover.Target>
              <Tooltip label="Edit API reference" withArrow>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={() => setOpened((o) => !o)}
                  aria-label="Edit API reference"
                >
                  <IconPencil size={14} />
                </ActionIcon>
              </Tooltip>
            </Popover.Target>
            <Popover.Dropdown>
              <Stack gap="xs">
                <Select
                  label="Method"
                  size="xs"
                  value={method}
                  data={METHODS.map((m) => ({
                    value: m,
                    label: m.toUpperCase(),
                  }))}
                  onChange={(v) =>
                    updateAttributes({ method: normalizeMethod(v) })
                  }
                />
                <TextInput
                  label="URL"
                  size="xs"
                  placeholder="https://api.example.com/v1/{apiKey}/…"
                  value={url}
                  onChange={(e) =>
                    updateAttributes({ url: e.currentTarget.value })
                  }
                />
              </Stack>
            </Popover.Dropdown>
          </Popover>
        )}
      </div>
    </NodeViewWrapper>
  );
}
